import { FunctionComponent, useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './LoginScreen.module.css';

type LoginTab = 'social' | 'phone' | 'enterprise';

const LoginScreen: FunctionComponent = () => {
  const [activeTab, setActiveTab] = useState<LoginTab>('social');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [workEmail, setWorkEmail] = useState('');
  const navigate = useNavigate();

  const handleGoogleLogin = () => {
    // TODO: Implement WorkOS Google SSO
    console.log('Google login clicked');
    // For now, navigate to home
    navigate('/');
  };

  const handleMicrosoftLogin = () => {
    // TODO: Implement WorkOS Microsoft SSO
    console.log('Microsoft login clicked');
    // For now, navigate to home
    navigate('/');
  };

  const handlePhoneLogin = () => {
    // TODO: Implement Twilio Verify OTP
    console.log('Phone login with:', phoneNumber);
    // For now, navigate to home if phone number exists
    if (phoneNumber) {
      navigate('/');
    }
  };

  const handleEnterpriseLogin = () => {
    // TODO: Implement WorkOS Enterprise SSO
    console.log('Enterprise SSO with:', workEmail);
    // For now, navigate to home if email exists
    if (workEmail) {
      navigate('/');
    }
  };

  const tabsRef = useRef<Array<HTMLButtonElement | null>>([]);

  useEffect(() => {
    // Focus first interactive control for accessibility
    tabsRef.current?.[0]?.focus();
  }, []);

  const onTabKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
      e.preventDefault();
      const idx = activeTab === 'social' ? 0 : activeTab === 'phone' ? 1 : 2;
      const next = e.key === 'ArrowRight' ? (idx + 1) % 3 : (idx + 2) % 3;
      const nextTab: LoginTab = next === 0 ? 'social' : next === 1 ? 'phone' : 'enterprise';
      setActiveTab(nextTab);
      tabsRef.current?.[next]?.focus();
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'social':
        return (
          <>
            <div className={styles.depth2Frame4}>
              <button className={styles.depth3Frame02} onClick={handleGoogleLogin} aria-label="Continue with Google">
                <svg className={styles.depth4Frame01} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <div className={styles.depth4Frame11}>
                  <b className={styles.continueWithGoogle}>Continue with Google</b>
                </div>
              </button>
            </div>
            <div className={styles.depth2Frame4}>
              <button className={styles.depth3Frame02} onClick={handleMicrosoftLogin} aria-label="Continue with Microsoft">
                <svg className={styles.depth4Frame01} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                  <path fill="#F25022" d="M11.4 11.4H1V1h10.4v10.4z"/>
                  <path fill="#00A4EF" d="M23 11.4H12.6V1H23v10.4z"/>
                  <path fill="#7FBA00" d="M11.4 23H1V12.6h10.4V23z"/>
                  <path fill="#FFB900" d="M23 23H12.6V12.6H23V23z"/>
                </svg>
                <div className={styles.depth4Frame11}>
                  <b className={styles.continueWithGoogle}>Continue with Microsoft</b>
                </div>
              </button>
            </div>
          </>
        );
      
      case 'phone':
        return (
          <div className={styles.phoneSection}>
            <div className={styles.inputContainer}>
              <label className="sr-only" htmlFor="login-phone-input">Phone number</label>
              <input
                type="tel"
                id="login-phone-input"
                placeholder="Enter your phone number"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                className={styles.phoneInput}
              />
            </div>
            <div className={styles.depth2Frame4}>
              <button 
                className={`${styles.depth3Frame02} ${phoneNumber ? styles.active : ''}`}
                onClick={handlePhoneLogin}
                disabled={!phoneNumber}
              >
                <div className={styles.depth4Frame11}>
                  <b className={styles.continueWithGoogle}>Send Verification Code</b>
                </div>
              </button>
            </div>
          </div>
        );
      
      case 'enterprise':
        return (
          <div className={styles.enterpriseSection}>
            <div className={styles.inputContainer}>
              <label className="sr-only" htmlFor="login-work-email">Work email</label>
              <input
                type="email"
                id="login-work-email"
                placeholder="Enter your work email"
                value={workEmail}
                onChange={(e) => setWorkEmail(e.target.value)}
                className={styles.emailInput}
              />
            </div>
            <div className={styles.depth2Frame4}>
              <button 
                className={`${styles.depth3Frame02} ${workEmail ? styles.active : ''}`}
                onClick={handleEnterpriseLogin}
                disabled={!workEmail}
              >
                <div className={styles.depth4Frame11}>
                  <b className={styles.continueWithGoogle}>Continue with SSO</b>
                </div>
              </button>
            </div>
          </div>
        );
    }
  };

  return (
    <div className={styles.loginScreen}>
      <div className={styles.depth0Frame0}>
        <div className={styles.depth1Frame2}>
          <div className={styles.depth2Frame0}>
            <div className={styles.depth3Frame0}>
              <div className={styles.brandName} aria-label="Mario Health">Mario Health</div>
              <b className={styles.welcome}>Healthcare shopping made easy</b>
            </div>
          </div>
          
          <div className={styles.depth2Frame3} role="tablist" aria-label="Sign in method">
            <div className={styles.depth3Frame01}>
              <button 
                ref={el => tabsRef.current[0] = el}
                role="tab"
                aria-selected={activeTab === 'social'}
                className={`${styles.depth4Frame0} ${activeTab === 'social' ? styles.activeTab : ''}`}
                onClick={() => setActiveTab('social')}
                onKeyDown={onTabKeyDown}
              >
                <div className={styles.depth5Frame0}>
                  <b className={styles.social}>Social</b>
                </div>
              </button>
              <button 
                ref={el => tabsRef.current[1] = el}
                role="tab"
                aria-selected={activeTab === 'phone'}
                className={`${styles.depth4Frame1} ${activeTab === 'phone' ? styles.activeTab : ''}`}
                onClick={() => setActiveTab('phone')}
                onKeyDown={onTabKeyDown}
              >
                <div className={styles.depth5Frame0}>
                  <b className={styles.social}>Phone</b>
                </div>
              </button>
              <button 
                ref={el => tabsRef.current[2] = el}
                role="tab"
                aria-selected={activeTab === 'enterprise'}
                className={`${styles.depth4Frame1} ${activeTab === 'enterprise' ? styles.activeTab : ''}`}
                onClick={() => setActiveTab('enterprise')}
                onKeyDown={onTabKeyDown}
              >
                <div className={styles.depth5Frame0}>
                  <b className={styles.social}>Enterprise SSO</b>
                </div>
              </button>
            </div>
          </div>

          {renderTabContent()}
        </div>
        
        <div className={styles.depth1Frame1}>
          <div className={styles.depth2Frame01}>
            <div className={styles.signupLink}>
              Don’t have an account? <a className={styles.signupText} href="https://mario.health/for-patients" target="_blank" rel="noreferrer">Join the waitlist</a>
            </div>
          </div>
          <div className={styles.depth2Frame1} />
        </div>
      </div>
    </div>
  );
};

export default LoginScreen;
